import React, { useRef, useState } from 'react';
import { Group, Rect, Text, Line } from 'react-konva';
import { SequenceFragment, ThemePalette, KonvaInteractionEvent } from '../../types';
import { SEQUENCE } from '../../constants/sequence';
import { FONT_FAMILY } from '../../constants/typography';
import type { SequenceAppearanceMode } from '../../utils/sequenceAppearance';
import { resolveFragmentAccentColor } from '../../utils/sequenceAppearance';

interface PeerBound {
    id: string;
    startY: number;
    endY: number;
}

interface FragmentComponentProps {
    fragment: SequenceFragment;
    x: number;
    width: number;
    isSelected: boolean;
    onFragmentClick: (id: string, e: KonvaInteractionEvent) => void;
    onFragmentDoubleClick: (id: string) => void;
    onFragmentDragStart?: (id: string) => void;
    /** Called on every drag-move with the live canvas Y so App.tsx can show a nesting hint */
    onFragmentDragMove?: (id: string, currentY: number) => void;
    onFragmentDragEnd?: (id: string, newStartY: number) => void;
    onResizeDragStart?: (id: string) => void;
    onResizeDragMove?: (id: string, newEndY: number, mode: 'strict' | 'squish' | 'push') => void;
    altDividerY?: number;
    altDividerBounds?: {
        minY: number;
        maxY: number;
        strictMinY?: number;
        strictMaxY?: number;
    };
    palette: ThemePalette;
    appearanceMode?: SequenceAppearanceMode;
    nestLevel?: number;
    /** Canvas-coord bounds of the parent fragment; constrains drag for nested fragments */
    parentBounds?: { startY: number; endY: number };
    /** Same-level peer fragments; used to prevent overlapping during drag */
    peerBounds?: PeerBound[];
    /** True when another fragment is being dragged over this one (would become child) */
    isPotentialParent?: boolean;
    /** Called when bottom resize handle drag ends; provides new endY in canvas coords and drag mode */
    onResizeEnd?: (id: string, newEndY: number, mode: 'strict' | 'squish' | 'push') => void;
    /** Called when ALT divider drag starts */
    onAltDividerDragStart?: (id: string) => void;
    /** Called while ALT divider is dragged */
    onAltDividerDragMove?: (id: string, newDividerY: number, mode: 'strict' | 'squish' | 'push') => void;
    /** Called when ALT divider drag ends; provides new divider Y in canvas coords and drag mode */
    onAltDividerDragEnd?: (id: string, newDividerY: number, mode: 'strict' | 'squish' | 'push') => void;
    /** Viewport pan/scale; used to convert screen Y -> canvas Y during resize */
    viewport: { x: number; y: number; scale: number };
    isReadOnly?: boolean;
}

const LABEL_HEIGHT = SEQUENCE.FRAGMENT_LABEL_HEIGHT;
const DRAG_HANDLE_WIDTH = 96;
const MIN_CANVAS_Y = 90;
const ALT_DIVIDER_TOP_GAP = 40;
const ALT_DIVIDER_BOTTOM_GAP = 28;

export const FragmentComponent: React.FC<FragmentComponentProps> = ({
    fragment,
    x,
    width,
    isSelected,
    onFragmentClick,
    onFragmentDoubleClick,
    onFragmentDragStart,
    onFragmentDragMove,
    onFragmentDragEnd,
    onResizeDragStart,
    onResizeDragMove,
    altDividerY,
    altDividerBounds,
    palette,
    appearanceMode = 'color',
    nestLevel = 0,
    parentBounds,
    peerBounds = [],
    isPotentialParent = false,
    onResizeEnd,
    onAltDividerDragStart,
    onAltDividerDragMove,
    onAltDividerDragEnd,
    viewport,
    isReadOnly = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isAdjustingDivider, setIsAdjustingDivider] = useState(false);
    const [liveEndY, setLiveEndY] = useState<number | null>(null);
    const [liveAltDividerY, setLiveAltDividerY] = useState<number | null>(null);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const dragOccurred = useRef(false);
    const lastDragYRef = useRef<number | null>(null);
    const handleHeight = 8;

    const height = fragment.endY - fragment.startY;
    const accentColor = resolveFragmentAccentColor(fragment.type, appearanceMode, palette);
    const typeLabelFill = appearanceMode === 'color' ? 'white' : palette.canvasBg;

    const borderColor = isPotentialParent
        ? accentColor
        : isSelected
            ? '#a855f7'
            : (isHovered || isDragging ? accentColor : palette.edgeDefault);

    const strokeWidth = isPotentialParent ? 2.5 : isSelected ? 2 : 1.5;
    const fillOpacity = isPotentialParent ? '1a' : nestLevel > 0 ? '12' : '08';

    /**
     * Given a raw Y (in canvas coords), clamp it so the fragment:
     * 1) stays below the sequence header,
     * 2) stays inside its parent (if nested),
     * 3) does not partially overlap same-level peers.
     */
    const computeConstrainedY = (rawY: number, dragDirection: -1 | 0 | 1): number => {
        const minY = parentBounds ? parentBounds.startY - SEQUENCE.FRAGMENT_NEST_HYSTERESIS : MIN_CANVAS_Y;
        const maxY = parentBounds ? parentBounds.endY - height + SEQUENCE.FRAGMENT_NEST_HYSTERESIS : Infinity;
        let y = Math.max(minY, Number.isFinite(maxY) ? Math.min(maxY, rawY) : rawY);
        if (peerBounds.length === 0) return y;

        const peers = [...peerBounds].sort((a, b) => a.startY - b.startY);
        let changed = true;
        let guard = 0;
        while (changed && guard < 16) {
            guard += 1;
            changed = false;
            for (const peer of peers) {
                if (y + height <= peer.startY || y >= peer.endY) continue;
                if (dragDirection > 0) {
                    y = peer.endY;
                } else if (dragDirection < 0) {
                    y = peer.startY - height;
                } else {
                    const distTop = Math.abs((y + height) - peer.startY);
                    const distBot = Math.abs(y - peer.endY);
                    y = distTop < distBot ? peer.startY - height : peer.endY;
                }
                y = Math.max(minY, Number.isFinite(maxY) ? Math.min(maxY, y) : y);
                changed = true;
            }
        }
        return y;
    };
    const computeConstrainedAltDividerY = (rawY: number): number => {
        const minDividerY = altDividerBounds?.minY ?? (fragment.startY + ALT_DIVIDER_TOP_GAP);
        const maxDividerY = altDividerBounds?.maxY ?? (fragment.endY - ALT_DIVIDER_BOTTOM_GAP);
        if (maxDividerY <= minDividerY) return fragment.startY + (fragment.endY - fragment.startY) / 2;
        return Math.max(minDividerY, Math.min(maxDividerY, rawY));
    };
    const computeStrictAltDividerY = (rawY: number): number => {
        const minDividerY = altDividerBounds?.strictMinY ?? altDividerBounds?.minY ?? (fragment.startY + ALT_DIVIDER_TOP_GAP);
        const maxDividerY = altDividerBounds?.strictMaxY ?? altDividerBounds?.maxY ?? (fragment.endY - ALT_DIVIDER_BOTTOM_GAP);
        if (maxDividerY <= minDividerY) return minDividerY;
        return Math.max(minDividerY, Math.min(maxDividerY, rawY));
    };
    const effectiveAltDividerY = liveAltDividerY ?? altDividerY;
    const getDividerDragMode = (event: MouseEvent): 'strict' | 'squish' | 'push' => {
        if (!event.shiftKey) return 'push';
        if (event.ctrlKey || event.metaKey || event.altKey) return 'strict';
        return 'squish';
    };
    const computePushAltDividerY = (rawY: number): number => {
        const minDividerY = altDividerBounds?.minY ?? (fragment.startY + ALT_DIVIDER_TOP_GAP);
        return Math.max(minDividerY, rawY);
    };

    return (
        <Group
            x={x}
            y={fragment.startY + dragOffsetY}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={(e) => {
                setIsHovered(false);
                const container = e.target.getStage()?.container();
                if (container && !isDragging && !isResizing && !isAdjustingDivider) container.style.cursor = 'default';
            }}
        >
            <Rect
                width={width}
                height={height}
                fill={`${accentColor}${fillOpacity}`}
                stroke={borderColor}
                strokeWidth={strokeWidth}
                dash={nestLevel > 0 ? [4, 3] : [6, 4]}
                cornerRadius={4}
                listening={false}
                perfectDrawEnabled={false}
                shadowForStrokeEnabled={false}
                shadowColor={isPotentialParent ? accentColor : undefined}
                shadowBlur={isPotentialParent ? 12 : 0}
                shadowOpacity={isPotentialParent ? 0.6 : 0}
            />

            <Rect width={width} height={height} fill="transparent" listening={false} perfectDrawEnabled={false} />

            <Rect
                x={0}
                y={0}
                width={Math.min(DRAG_HANDLE_WIDTH, width)}
                height={LABEL_HEIGHT}
                fill="transparent"
                draggable={!isReadOnly}
                dragBoundFunc={(pos) => ({ x: 0, y: pos.y })}
                onMouseEnter={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container && !isResizing && !isAdjustingDivider) container.style.cursor = 'grab';
                }}
                onMouseLeave={(e) => {
                    const container = e.target.getStage()?.container();
                    if (container && !isDragging && !isResizing && !isAdjustingDivider) container.style.cursor = 'default';
                }}
                onDragStart={(e) => {
                    dragOccurred.current = false;
                    setIsDragging(true);
                    lastDragYRef.current = null;
                    const container = e.target.getStage()?.container();
                    if (container) container.style.cursor = 'grabbing';
                    onFragmentDragStart?.(fragment.id);
                    e.target.position({ x: 0, y: 0 });
                }}
                onDragMove={(e) => {
                    e.target.x(0);
                    const proposedY = fragment.startY + e.target.y();
                    const prevY = lastDragYRef.current;
                    const dragDirection: -1 | 0 | 1 = prevY === null
                        ? 0
                        : (proposedY > prevY + 0.5 ? 1 : (proposedY < prevY - 0.5 ? -1 : 0));
                    const constrainedY = computeConstrainedY(proposedY, dragDirection);
                    lastDragYRef.current = constrainedY;
                    setDragOffsetY(constrainedY - fragment.startY);
                    dragOccurred.current = true;
                    onFragmentDragMove?.(fragment.id, constrainedY);
                }}
                onDragEnd={(e) => {
                    setIsDragging(false);
                    const proposedY = fragment.startY + e.target.y();
                    const prevY = lastDragYRef.current;
                    const dragDirection: -1 | 0 | 1 = prevY === null
                        ? 0
                        : (proposedY > prevY + 0.5 ? 1 : (proposedY < prevY - 0.5 ? -1 : 0));
                    const constrainedY = computeConstrainedY(proposedY, dragDirection);
                    lastDragYRef.current = null;
                    e.target.position({ x: 0, y: 0 });
                    setDragOffsetY(0);
                    onFragmentDragMove?.(fragment.id, -1);
                    const container = e.target.getStage()?.container();
                    if (container && !isResizing && !isAdjustingDivider) container.style.cursor = isHovered ? 'grab' : 'default';
                    if (!dragOccurred.current) return;
                    onFragmentDragEnd?.(fragment.id, constrainedY);
                }}
                onClick={(e) => {
                    if (dragOccurred.current) { dragOccurred.current = false; return; }
                    onFragmentClick(fragment.id, e);
                }}
                onTap={(e) => {
                    if (dragOccurred.current) { dragOccurred.current = false; return; }
                    onFragmentClick(fragment.id, e);
                }}
                onDblClick={() => onFragmentDoubleClick(fragment.id)}
                onDblTap={() => onFragmentDoubleClick(fragment.id)}
            />

            <Rect
                width={Math.min(80, width)}
                height={LABEL_HEIGHT}
                fill={accentColor}
                cornerRadius={[4, 0, 4, 0]}
                opacity={isDragging ? 1 : isHovered || isPotentialParent ? 0.95 : 0.85}
                listening={false}
                perfectDrawEnabled={false}
            />
            <Text
                text={fragment.type.toUpperCase()}
                x={6}
                y={0}
                height={LABEL_HEIGHT}
                verticalAlign="middle"
                fontFamily={FONT_FAMILY}
                fontSize={10}
                fontStyle="bold"
                fill={typeLabelFill}
                listening={false}
                perfectDrawEnabled={false}
            />

            {(isHovered || isDragging) && (
                <Text
                    text=":::"
                    x={Math.min(80, width) - 18}
                    y={1}
                    height={LABEL_HEIGHT}
                    verticalAlign="middle"
                    fontSize={11}
                    fill="rgba(255,255,255,0.65)"
                    listening={false}
                />
            )}

            {isPotentialParent && (
                <Text
                    text="nest inside v"
                    x={Math.min(80, width) + 12}
                    y={0}
                    height={LABEL_HEIGHT}
                    verticalAlign="middle"
                    fontFamily={FONT_FAMILY}
                    fontSize={10}
                    fontStyle="bold"
                    fill={accentColor}
                    listening={false}
                />
            )}

            {fragment.label && !isPotentialParent && (
                <>
                    <Rect
                        x={Math.min(80, width) + 4}
                        y={3}
                        width={Math.max(40, width - (Math.min(80, width) + 12))}
                        height={LABEL_HEIGHT - 6}
                        fill={palette.canvasBg}
                        opacity={0.34}
                        cornerRadius={4}
                        listening={false}
                    />
                    <Text
                        text={`[${fragment.label}]`}
                        x={Math.min(80, width) + 8}
                        y={0}
                        height={LABEL_HEIGHT}
                        verticalAlign="middle"
                        fontFamily={FONT_FAMILY}
                        fontSize={11}
                        fill={palette.nodeText}
                        opacity={0.82}
                        listening={false}
                    />
                </>
            )}

            {nestLevel > 0 && (
                <Rect
                    x={0}
                    y={LABEL_HEIGHT}
                    width={3}
                    height={height - LABEL_HEIGHT}
                    fill={accentColor}
                    opacity={0.35}
                    cornerRadius={[0, 0, 0, 4]}
                    listening={false}
                />
            )}

            {!isReadOnly && fragment.type === 'alt' && effectiveAltDividerY !== undefined && (
                <>
                    <Line
                        points={[0, effectiveAltDividerY - fragment.startY, width, effectiveAltDividerY - fragment.startY]}
                        stroke={borderColor}
                        strokeWidth={1}
                        dash={[6, 4]}
                        listening={false}
                        perfectDrawEnabled={false}
                        opacity={0.55}
                    />
                    <Rect
                        x={0}
                        y={effectiveAltDividerY - fragment.startY - 7}
                        width={width}
                        height={14}
                        fill="transparent"
                        onMouseEnter={(e) => {
                            const container = e.target.getStage()?.container();
                            if (container) container.style.cursor = 'ns-resize';
                        }}
                        onMouseLeave={(e) => {
                            if (isAdjustingDivider) return;
                            const container = e.target.getStage()?.container();
                            if (container) container.style.cursor = isHovered ? 'grab' : 'default';
                        }}
                        onMouseDown={(e) => {
                            e.cancelBubble = true;
                            const stage = e.target.getStage();
                            if (!stage || effectiveAltDividerY === undefined || !onAltDividerDragEnd) return;
                            setIsAdjustingDivider(true);
                            const container = stage.container();
                            if (container) container.style.cursor = 'ns-resize';
                            onAltDividerDragStart?.(fragment.id);

                            const onMove = (me: MouseEvent) => {
                                const canvasY = (me.clientY - stage.container().getBoundingClientRect().top - viewport.y) / viewport.scale;
                                const mode = getDividerDragMode(me);
                                const nextDividerY = mode === 'strict'
                                    ? computeStrictAltDividerY(canvasY)
                                    : (mode === 'push' ? computePushAltDividerY(canvasY) : computeConstrainedAltDividerY(canvasY));
                                setLiveAltDividerY(nextDividerY);
                                onAltDividerDragMove?.(fragment.id, nextDividerY, mode);
                            };
                            const onUp = (me: MouseEvent) => {
                                window.removeEventListener('mousemove', onMove);
                                window.removeEventListener('mouseup', onUp);
                                setIsAdjustingDivider(false);
                                if (container) container.style.cursor = 'default';
                                const canvasY = (me.clientY - stage.container().getBoundingClientRect().top - viewport.y) / viewport.scale;
                                const mode = getDividerDragMode(me);
                                const newDividerY = mode === 'strict'
                                    ? computeStrictAltDividerY(canvasY)
                                    : (mode === 'push' ? computePushAltDividerY(canvasY) : computeConstrainedAltDividerY(canvasY));
                                setLiveAltDividerY(null);
                                onAltDividerDragEnd(fragment.id, newDividerY, mode);
                            };
                            window.addEventListener('mousemove', onMove);
                            window.addEventListener('mouseup', onUp);
                        }}
                    />
                    <Rect
                        x={Math.max(6, width / 2 - 28)}
                        y={effectiveAltDividerY - fragment.startY - 9}
                        width={56}
                        height={18}
                        cornerRadius={9}
                        fill={palette.canvasBg}
                        opacity={0.28}
                        listening={false}
                    />
                    <Rect
                        x={Math.max(8, width / 2 - 16)}
                        y={effectiveAltDividerY - fragment.startY - 4}
                        width={32}
                        height={8}
                        cornerRadius={4}
                        fill={accentColor}
                        opacity={0.65}
                        listening={false}
                        perfectDrawEnabled={false}
                    />
                </>
            )}

            {!isReadOnly && (isHovered || isSelected || isResizing) && (
                <Rect
                    x={width * 0.1}
                    y={height - handleHeight / 2}
                    width={width * 0.8}
                    height={handleHeight}
                    fill={accentColor}
                    opacity={isResizing ? 0.8 : 0.4}
                    cornerRadius={3}
                    onMouseEnter={(e) => {
                        const container = e.target.getStage()?.container();
                        if (container) container.style.cursor = 'ns-resize';
                    }}
                    onMouseLeave={(e) => {
                        if (isResizing) return;
                        const container = e.target.getStage()?.container();
                        if (container) container.style.cursor = isHovered ? 'grab' : 'default';
                    }}
                    onMouseDown={(e) => {
                        e.cancelBubble = true;
                        const stage = e.target.getStage();
                        if (!stage) return;
                        setIsResizing(true);
                        const container = stage.container();
                        if (container) container.style.cursor = 'ns-resize';
                        onResizeDragStart?.(fragment.id);

                        const onMove = (me: MouseEvent) => {
                            const canvasY = (me.clientY - stage.container().getBoundingClientRect().top - viewport.y) / viewport.scale;
                            const minEndY = fragment.startY + LABEL_HEIGHT + 20;
                            const mode = getDividerDragMode(me);
                            const nextEndY = Math.max(minEndY, canvasY);
                            setLiveEndY(nextEndY);
                            onResizeDragMove?.(fragment.id, nextEndY, mode);
                        };
                        const onUp = (me: MouseEvent) => {
                            window.removeEventListener('mousemove', onMove);
                            window.removeEventListener('mouseup', onUp);
                            setIsResizing(false);
                            setLiveEndY(null);
                            if (container) container.style.cursor = 'default';
                            const canvasY = (me.clientY - stage.container().getBoundingClientRect().top - viewport.y) / viewport.scale;
                            const newEndY = Math.max(fragment.startY + LABEL_HEIGHT + 20, canvasY);
                            const mode = getDividerDragMode(me);
                            if (onResizeEnd) onResizeEnd(fragment.id, newEndY, mode);
                        };
                        window.addEventListener('mousemove', onMove);
                        window.addEventListener('mouseup', onUp);
                    }}
                />
            )}

            {isResizing && liveEndY !== null && (
                <Line
                    points={[0, liveEndY - fragment.startY, width, liveEndY - fragment.startY]}
                    stroke={accentColor}
                    strokeWidth={2}
                    dash={[6, 4]}
                    listening={false}
                    opacity={0.8}
                />
            )}
        </Group>
    );
};
